import styles from './index.module.css'

import React from 'react'

import {
  FilterComponentProps,
  FilterOperator,
  FilterOperatorOption,
} from 'types/filter-page'

import { Flex, Text, TextField } from 'components/ds'

import { useKeyPress } from 'hooks/use-key-press'

import { FilterOperatorSelector } from '../../filter-operator-selector'
import { getBetweenRangeErrors } from '../../utils'
import { FilterErrorText } from '../components'

const DateForm = ({
  label,
  value,
  onChange,
  operator = 'equals',
  onPress,
}: FilterComponentProps) => {
  const { handleKeyDown } = useKeyPress('Enter', onPress)

  const {
    startMissing,
    endMissing,
    isIncomplete: isBetweenIncomplete,
  } = getBetweenRangeErrors(value)

  // Helper function to create date inputs
  const createDateInput = (
    inputValue: string,
    onChangeHandler: (_value: string) => void,
    shouldAutoFocus = true,
    hasError = false,
    doubleInput = false,
  ) => (
    <TextField.Root
      type="date"
      value={inputValue || ''}
      onChange={e => onChangeHandler(e.target.value)}
      onKeyDown={handleKeyDown}
      placeholder={label}
      autoFocus={shouldAutoFocus}
      color={hasError ? 'red' : undefined}
      style={{
        width: doubleInput ? '125px' : '273px',
        ...(hasError ? { boxShadow: 'inset 0 0 0 1px var(--red-8)' } : {}),
      }}
      className={styles.dateInput}
    />
  )

  const allFilterOperators: FilterOperatorOption[] = [
    {
      value: 'equals',
      component: createDateInput(value || '', newValue => {
        onChange(newValue, operator)
      }),
    },
    {
      value: 'between',
      component: (
        <Flex direction="column" gap="1">
          <Flex gap="2" align="center">
            {createDateInput(
              value?.[0] || '',
              newValue => {
                const updatedValue = !newValue
                  ? ['', value?.[1] || '']
                  : [newValue, value?.[1] || '']

                onChange(updatedValue, operator)
              },
              true,
              startMissing,
              true,
            )}
            <Text size="2">-</Text>
            {createDateInput(
              value?.[1] || '',
              newValue => {
                const updatedValue = !newValue
                  ? [value?.[0] || '', '']
                  : [value?.[0] || '', newValue]

                onChange(updatedValue, operator)
              },
              false,
              endMissing,
              true,
            )}
          </Flex>
          {isBetweenIncomplete && (
            <FilterErrorText>Enter both dates</FilterErrorText>
          )}
        </Flex>
      ),
    },
    {
      value: 'lte',
      component: createDateInput(value || '', newValue => {
        onChange(newValue, operator)
      }),
    },
    {
      value: 'gte',
      component: createDateInput(value || '', newValue => {
        onChange(newValue, operator)
      }),
    },
  ]

  return (
    <FilterOperatorSelector<FilterOperator>
      operator={operator}
      onChange={onChange}
      operators={allFilterOperators}
    />
  )
}

export default DateForm
